// src/contexts/AuthContext.js
import React, { createContext, useState, useEffect, useCallback } from "react";
import api from "../axiosConfig";
import { useNavigate } from "react-router-dom";

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // ===============================
  // LOGOUT
  // ===============================
  const logout = useCallback(() => {
    localStorage.removeItem("token");
    localStorage.removeItem("userRole");

    delete api.defaults.headers.common["Authorization"];

    setCurrentUser(null);
    setLoading(false);

    navigate("/login", { replace: true });
  }, [navigate]);

  // ===============================
  // FETCH USER DATA
  // ===============================
  const fetchUserData = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");

      if (!token) {
        setLoading(false);
        return;
      }

      api.defaults.headers.common["Authorization"] = `Bearer ${token}`;

      const res = await api.get("/auth/me");

      const user = res?.data?.user || res?.data;

      if (!user || !user.username) {
        console.error("User data invalid:", res.data);
        return logout();
      }

      setCurrentUser({
        id: user.id,
        username: user.username,
        role: user.role,
        sector_id: user.sector_id ?? null,
        sector_name: user.sector_name ?? null,
      });

      localStorage.setItem("userRole", user.role || "");
    } catch (err) {
      console.error("AUTH ME ERROR:", err.response?.data || err.message);
      logout();
    } finally {
      setLoading(false);
    }
  }, [logout]);

  // ===============================
  // FIRST LOAD
  // ===============================
  useEffect(() => {
    fetchUserData();
  }, [fetchUserData]);

  // ===============================
  // LOGIN
  // ===============================
  const login = async (username, password) => {
    try {
      const res = await api.post("/auth/login", { username, password });

      const token = res.data?.token;

      if (!token) {
        throw new Error("Token tidak ditemukan");
      }

      localStorage.setItem("token", token);

      api.defaults.headers.common["Authorization"] = `Bearer ${token}`;

      const user = res.data?.user || res.data;

      if (!user || !user.username) {
        throw new Error("User data tidak valid");
      }

      setCurrentUser({
        id: user.id,
        username: user.username,
        role: user.role,
        sector_id: user.sector_id ?? null,
        sector_name: user.sector_name ?? null,
      });

      localStorage.setItem("userRole", user.role);

      return { success: true, role: user.role };
    } catch (err) {
      console.error("LOGIN ERROR:", err.response?.data || err.message);

      return {
        success: false,
        error:
          err.response?.data?.message ||
          err.response?.data?.msg ||
          err.message ||
          "Terjadi kesalahan login",
      };
    }
  };

  // ===============================
  // CHECK ROLE
  // ===============================
  const hasRole = (role) => currentUser?.role === role;

  return (
    <AuthContext.Provider
      value={{ currentUser, loading, login, logout, hasRole }}
    >
      {children}
    </AuthContext.Provider>
  );
};
